import { useState, useEffect, useCallback } from 'react';
import { api, getApiErrorMessage, isNotFoundError } from '../services/api';

export interface Word {
  id: number;
  topicId: number;
  turkish: string;
  russian: string | null;
  transcription: string | null;
  audioPath: string | null;
  imagePath: string | null;
  audioStatus: 'pending' | 'generating' | 'done' | 'error';
  imageStatus: 'pending' | 'generating' | 'done' | 'error';
  translationStatus: 'pending' | 'generating' | 'done' | 'error';
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export type JobPhase = 'translation' | 'audio' | 'image';

export interface JobProgress {
  status: 'pending' | 'running' | 'completed' | 'failed';
  phase: JobPhase | null;
  phaseProgress: { completed: number; failed: number; total: number };
  completedWords: number;
  failedWords: number;
  totalWords: number;
  errors: string[];
}

export interface PollJobOptions {
  intervalMs?: number;
  maxAttempts?: number;
  timeoutMs?: number;
  onProgress?: (progress: JobProgress) => void;
}

// Jobs live in server memory: a restart makes them 404 forever. Polling must be bounded,
// otherwise one dead job id locks the word table for the rest of the session.
const POLL_INTERVAL_MS = 1500;
const POLL_MAX_ATTEMPTS = 600; // ~15 minutes
const POLL_TIMEOUT_MS = 15 * 60 * 1000;
const POLL_MAX_CONSECUTIVE_ERRORS = 5;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const toJobProgress = (raw: Partial<JobProgress> | undefined): JobProgress => ({
  status: raw?.status ?? 'running',
  phase: raw?.phase ?? null,
  phaseProgress: {
    completed: raw?.phaseProgress?.completed ?? 0,
    failed: raw?.phaseProgress?.failed ?? 0,
    total: raw?.phaseProgress?.total ?? 0,
  },
  completedWords: raw?.completedWords ?? 0,
  failedWords: raw?.failedWords ?? 0,
  totalWords: raw?.totalWords ?? 0,
  errors: raw?.errors ?? [],
});

export function useWords(topicId: number | undefined) {
  const [words, setWords] = useState<Word[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWords = useCallback(async () => {
    if (!topicId) return;
    setLoading(true);
    try {
      const res = await api.get(`/topics/${topicId}/words`);
      setWords(res.data.data);
      setError(null);
    } catch (err: unknown) {
      // Without this a failed request renders as "слов пока нет".
      setError(getApiErrorMessage(err, 'Не удалось загрузить список слов'));
    } finally {
      setLoading(false);
    }
  }, [topicId]);

  useEffect(() => {
    fetchWords();
  }, [fetchWords]);

  const addWord = async (data: { turkish: string; russian?: string; transcription?: string }) => {
    const res = await api.post(`/topics/${topicId}/words`, data);
    setWords((prev) => [...prev, res.data.data]);
    return res.data.data;
  };

  const updateWord = async (wordId: number, data: Partial<Word>) => {
    const res = await api.patch(`/words/${wordId}`, data);
    setWords((prev) => prev.map((w) => (w.id === wordId ? res.data.data : w)));
    return res.data.data;
  };

  const deleteWord = async (wordId: number) => {
    await api.delete(`/words/${wordId}`);
    setWords((prev) => prev.filter((w) => w.id !== wordId));
  };

  const addWordsBulk = async (items: { turkish: string; russian?: string }[]) => {
    const res = await api.post(`/topics/${topicId}/words/bulk`, { words: items });
    setWords((prev) => [...prev, ...res.data.data]);
    return res.data.data;
  };

  const parsePaste = async (text: string) => {
    const res = await api.post(`/topics/${topicId}/words/paste`, { text });
    return res.data.data as { turkish: string; russian?: string }[];
  };

  const parseUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(`/topics/${topicId}/words/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.data as { turkish: string; russian?: string }[];
  };

  const generateTranslations = async (wordIds?: number[]) => {
    const res = await api.post(`/topics/${topicId}/generate/translations`, { wordIds });
    return res.data.data as { jobId: string; status: string; totalWords: number };
  };

  const generateAudio = async (wordIds?: number[]) => {
    const res = await api.post(`/topics/${topicId}/generate/audio`, { wordIds });
    return res.data.data as { jobId: string; status: string; totalWords: number };
  };

  const generateImages = async (wordIds?: number[]) => {
    const res = await api.post(`/topics/${topicId}/generate/images`, { wordIds });
    return res.data.data as { jobId: string; status: string; totalWords: number };
  };

  const generateAll = async (wordIds?: number[]) => {
    const res = await api.post(`/topics/${topicId}/generate/all`, { wordIds });
    return res.data.data as { jobId: string; status: string; totalWords: number };
  };

  /**
   * Polls a generation job until it settles. Rejects (and sets `error`) once the
   * attempts/timeout budget is spent or the job disappears, so the caller can never be
   * left waiting forever. `completedWords`/`failedWords` are cumulative across phases,
   * so live progress comes from `phaseProgress`.
   */
  const pollJob = useCallback(async (jobId: string, options: PollJobOptions = {}): Promise<JobProgress> => {
    const intervalMs = options.intervalMs ?? POLL_INTERVAL_MS;
    const maxAttempts = options.maxAttempts ?? POLL_MAX_ATTEMPTS;
    const timeoutMs = options.timeoutMs ?? POLL_TIMEOUT_MS;
    const deadline = Date.now() + timeoutMs;
    let consecutiveErrors = 0;

    setError(null);

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      if (Date.now() >= deadline) {
        const message = 'Генерация не завершилась вовремя. Проверьте статус вручную и запустите повторно.';
        setError(message);
        throw new Error(message);
      }

      try {
        const res = await api.get(`/jobs/${jobId}`);
        const progress = toJobProgress(res.data.data as Partial<JobProgress>);
        consecutiveErrors = 0;
        options.onProgress?.(progress);

        if (progress.status === 'completed' || progress.status === 'failed') {
          return progress;
        }
      } catch (err: unknown) {
        consecutiveErrors += 1;
        // A missing job (server restart) will never come back — stop immediately
        // instead of hammering the endpoint for the whole budget.
        if (isNotFoundError(err)) {
          const message =
            'Задача генерации потеряна (сервер был перезапущен). Запустите генерацию заново.';
          setError(message);
          throw new Error(message);
        }
        if (consecutiveErrors >= POLL_MAX_CONSECUTIVE_ERRORS) {
          const message = getApiErrorMessage(err, 'Не удалось получить статус задачи генерации');
          setError(message);
          throw new Error(message);
        }
      }

      await sleep(intervalMs);
    }

    const message = 'Генерация не завершилась: превышено время ожидания. Запустите её заново.';
    setError(message);
    throw new Error(message);
  }, []);

  return {
    words,
    loading,
    error,
    fetchWords,
    addWord,
    updateWord,
    deleteWord,
    addWordsBulk,
    parsePaste,
    parseUpload,
    generateTranslations,
    generateAudio,
    generateImages,
    generateAll,
    pollJob,
  };
}